/**
 * 变换会话（拖动 / 手柄缩放 / 旋转）—— 状态机与坐标数学，**不依赖真实布局**。
 *
 * 设计要点（决定它可以在 jsdom 里被完整测试）：
 *  · 屏幕坐标 → 用户坐标由外部注入（生产用 `screenToRoot`，测试用恒等映射）
 *  · 预览 = 直接写元素的 transform 属性（性能需要），但**提交前一定先回滚**，
 *    再由 History 应用一次真正的命令 —— 于是每次落盘的变更都只有一条 undo 记录
 */
import { type Mat, type Pt, type Rect, translate } from '@/core/geom/matrix';
import { type HandleId, nextTransformAttribute, resizeWorldMatrix, rotateWorldMatrix, snapValue } from '@/core/geom/handles';
import type { Command } from '@/core/history';
import { applyWorldTransform, type TransformDeps } from '@/core/ops/transform';

export type SessionKind = 'move' | 'resize' | 'rotate';

export interface SessionMods {
  shift?: boolean;
  alt?: boolean;
  /** 网格吸附（0 = 关闭） */
  grid?: number;
}

export interface PreviewPlan {
  el: Element;
  orig: string | null;
  parentMat: Mat;
}

export interface TransformSession {
  kind: SessionKind;
  /** 会话开始时的选择框（根用户坐标） */
  box: Rect;
  startRoot: Pt;
  handle?: HandleId;
  plans: PreviewPlan[];
}

const LABEL: Record<SessionKind, string> = { move: '移动', resize: '缩放', rotate: '旋转' };
const ROTATE_SNAP_DEG = 15;

export function capturePlans(
  els: readonly Element[],
  parentMatOf: (el: Element) => Mat,
): PreviewPlan[] {
  return els.map((el) => ({ el, orig: el.getAttribute('transform'), parentMat: parentMatOf(el) }));
}

export function beginSession(
  kind: SessionKind,
  els: readonly Element[],
  box: Rect,
  startRoot: Pt,
  parentMatOf: (el: Element) => Mat,
  handle?: HandleId,
): TransformSession {
  return { kind, box, startRoot, handle, plans: capturePlans(els, parentMatOf) };
}

/** 当前应施加到选中集的世界矩阵（含 Shift / Alt / 网格吸附） */
export function sessionWorld(session: TransformSession, curRoot: Pt, mods: SessionMods = {}): Mat {
  const { box, startRoot } = session;
  switch (session.kind) {
    case 'move': {
      let dx = curRoot.x - startRoot.x;
      let dy = curRoot.y - startRoot.y;
      if (mods.shift) {
        // 轴向约束：取位移较大的轴
        if (Math.abs(dx) >= Math.abs(dy)) dy = 0;
        else dx = 0;
      }
      const grid = mods.grid ?? 0;
      if (grid > 0) {
        dx = snapValue(box.x + dx, grid) - box.x;
        dy = snapValue(box.y + dy, grid) - box.y;
      }
      return translate(dx, dy);
    }
    case 'resize':
      return resizeWorldMatrix({
        handle: session.handle ?? 'se',
        start: startRoot,
        cur: curRoot,
        box,
        uniform: mods.shift,
        fromCenter: mods.alt,
      });
    case 'rotate':
      return rotateWorldMatrix({
        center: { x: box.x + box.w / 2, y: box.y + box.h / 2 },
        start: startRoot,
        cur: curRoot,
        snapDeg: mods.shift ? ROTATE_SNAP_DEG : 0,
      });
  }
}

/** 预览：直接改属性（唯一被允许的例外，见文件头说明） */
export function applyPreview(plans: readonly PreviewPlan[], world: Mat): void {
  for (const plan of plans) {
    plan.el.setAttribute('transform', nextTransformAttribute(plan.orig, world, plan.parentMat));
  }
}

export function restorePreview(plans: readonly PreviewPlan[]): void {
  for (const plan of plans) {
    if (plan.orig === null) plan.el.removeAttribute('transform');
    else plan.el.setAttribute('transform', plan.orig);
  }
}

/**
 * 提交：先回滚预览（让 patch 的 before 与真实 DOM 一致），
 * 再由 History 应用一次命令 —— 全过程只产生一条 undo。
 */
export function commitSession(
  session: TransformSession,
  world: Mat,
  deps: TransformDeps,
  execute: (cmd: Command) => boolean,
): boolean {
  restorePreview(session.plans);
  const cmd = applyWorldTransform(
    deps,
    session.plans.map((p) => p.el),
    world,
    LABEL[session.kind],
  );
  if (cmd.patches.length === 0) return false; // 零位移/恒等变换：不产生命令
  return execute(cmd);
}

/** 取消（Esc）：回滚预览，不入历史 */
export function cancelSession(session: TransformSession): void {
  restorePreview(session.plans);
}
